import { BadgeStatus, Nav } from "@deere/fuel-react";
import { useHistory, useLocation } from "react-router-dom";
import {
  EXTENSION_URI,
  ROUTE_EXTENSION,
  ROUTE_EXTENSION_BP,
  ROUTE_DM,
  ROUTE_EXTENSION_ERRORS,
  ROUTE_EXTENSION_JDF,
  ROUTE_EXTENSION_LOGS,
  ROUTE_EXTENSION_MANUAL_REQUEST,
  APP_VIEW,
  ROUTE_IB_BKP,
  ROLES,
  ROUTE_IB_CCM,
  ROUTE_IB_BP,
  ROUTE_IB_CACLC,
  ROUTE_BN,
  ROUTE_BN_ERROR,
  ROUTE_BN_ACCOUNTING,
  ROUTE_BN_UPLOAD,
  ROUTE_BN_SUCESS,
  ROUTE_REPORT,
  ROUTE_REPORT_BANK,
  ROUTE_REPORT_DEALER,
  ROUTE_REPORT_MONTHLY_EXTENSION,
  ROUTE_REPORT_MONTHLY_EXTENSION_IEEFTI,
  ROUTE_REPORT_CUSTOMER_INTEREST_DRILLDOWN,
  ROUTE_IB,
  STORED_ROUTE_REPORT,
  STORED_ROUTE_IB,
  STORED_ROUTE_EXT,
  MANUAL_REQUEST_LIST,
} from "../constants/constant";
import { useTranslation } from "./translation-context";
import styles from "../css/navbar.module.css";
import Banner from "./Banner";
import BadgeStat from "./BadgeStat";
import { getDataWAuth } from "../utils/api-service";
import React, { useEffect, useContext, useCallback, useState } from "react";
import ErrorContext from "../common/error-context";
import ToastMessageFunction from "../common/toast-message-function";
import { checkErrorMsg, getUserRole } from "../utils/utils";
import { useSelector } from "react-redux";

const EXT_BANNER_MSG =
  "The scheduler will run every hour from 6:00 AM to 5:00 PM CET.";
const IB_CCM_BANNER_MSG =
  "Updating the Dealer Rate will override all Rates based on Reason Codes.";

export default function TopNav() {
  const errCxt = useContext(ErrorContext);
  const history = useHistory();
  const pathName = history && history.location && history.location.pathname;

  const location = useLocation();
  const { translate: t } = useTranslation();
  const toastManager = ToastMessageFunction();
  const userData = useSelector((state) => state.userState.userData);
  
  // Check if user has accounting member RACF access
  const isAccountingMember = userData?.authorities?.includes("U4NBNACC") ?? false;
  const [activeItem, setActiveItem] = useState(() => {
    // Get user role first
    const currentRole = getUserRole(userData?.userData);

    // First priority: determine activeItem from current URL path
    const currentPath = window.location.pathname;

    // Block BN users from extension paths
    if (currentPath.startsWith("/extension") &&
        (currentRole === ROLES.BN_ADMIN || currentRole === ROLES.JDU_ADMIN || currentRole === ROLES.BN_USER)) {
      return currentRole;
    }
    if (currentPath.startsWith("/extension")) return "EXT";
    if (currentPath.startsWith("/interestBearing")) return "IB";
    if (currentPath.startsWith("/dataManagement")) return "DM";
    if (currentPath.startsWith("/report")) return "REPORT";
    if (currentPath.startsWith("/dashBoard")) return "DASHBOARD";
    if (currentPath.startsWith("/billnow")) {
      if (currentRole === ROLES.BN_ADMIN) return ROLES.BN_ADMIN;
      if (currentRole === ROLES.JDU_ADMIN) return ROLES.JDU_ADMIN;
      if (currentRole === ROLES.BN_USER) return ROLES.BN_USER;
    }

    // Second priority: check sessionStorage
    if (
      sessionStorage.getItem("activeItem") !== "null" &&
      sessionStorage.getItem("activeItem") !== null &&
      sessionStorage.getItem("activeItem") !== undefined
    ) {
      const storedItem = sessionStorage.getItem("activeItem");

      // Prevent BN users from using stored "EXT" activeItem
      if (
        storedItem === "EXT" &&
        (currentRole === ROLES.BN_ADMIN ||
          currentRole === ROLES.JDU_ADMIN ||
          currentRole === ROLES.BN_USER)
      ) {
        return currentRole;
      }

      // If stored activeItem is DASHBOARD but user is now BKP, switch to IB
      if (storedItem === "DASHBOARD" && currentRole === ROLES.BKP) {
        return "IB";
      }
      return storedItem;
    }

    // Last priority: default based on role
    if (currentRole === ROLES.BN_ADMIN) return ROLES.BN_ADMIN;
    else if (currentRole === ROLES.JDU_ADMIN) return ROLES.JDU_ADMIN;
    else if (currentRole === ROLES.BN_USER) return ROLES.BN_USER;
    else if (currentRole === ROLES.BKP) return "IB";
    else if (currentRole === ROLES.BP)
      return "DASHBOARD"; // BP defaults to dashboard
    else if (currentRole === ROLES.ADMIN)
      return "DASHBOARD"; // ADMIN defaults to dashboard
    else if (currentRole === ROLES.CCM)
      return "DASHBOARD"; // CCM defaults to dashboard
    else return "EXT"; // default to Extension for non-BKP/non-BP
  });

  const [showBanner, setShowBanner] = useState(true);
  const [manualNewRequestCount, setManualNewRequestCount] = useState(0);

  // Watch for user role changes and redirect appropriately
  useEffect(() => {
    if (userData?.userData) {
      const userRole = getUserRole(userData);

      // Redirect BKP away from dashboard
      if (userRole === ROLES.BKP && activeItem === "DASHBOARD") {
        setActiveItem("IB");
        history.push(ROUTE_IB_BKP);
      }

      // Redirect BN_ADMIN, JDU_ADMIN away from Extension
      if (
        (userRole === ROLES.BN_ADMIN || userRole === ROLES.JDU_ADMIN) &&
        activeItem === "EXT"
      ) {
        setActiveItem(userRole);
        history.push(ROUTE_BN_ERROR);
      }

      // Redirect BN_USER away from Extension
      if (userRole === ROLES.BN_USER && activeItem === "EXT") {
        setActiveItem(userRole);
        // BN_USER with accounting RACF goes to accounting-approval, otherwise review-approve
        if (isAccountingMember) {
          history.push(ROUTE_BN_ACCOUNTING);
        } else {
          history.push(ROUTE_BN_ERROR);
        }
      }
    }
  }, [userData?.userData, activeItem, history]);

  useEffect(() => {
    if (pathName.includes("extension")) {
      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 5000);

      return () => clearTimeout(timer);
    } else if (pathName.includes("ratesAllocation")) {
      setShowBanner(true);
    }
  }, [pathName]);

  useEffect(() => {
    sessionStorage.setItem("activeItem", activeItem);

    // Get current pathname to check if we're already on the right page
    const currentPath = location.pathname;
    const currentRole = getUserRole(userData);
    console.log(
      "Navigating to activeItem:",
      activeItem,
      currentPath,
      currentRole,
    );

    // Dashboard navigation active only for BP, ADMIN, and CCM.
    if (activeItem === "DASHBOARD") {
      const role = getUserRole(userData);
      if (role === ROLES.BP && !currentPath.startsWith("/dashBoard")) {
        history.push("/dashBoardBP");
      }
      if (role === ROLES.ADMIN && !currentPath.startsWith("/dashBoard")) {
        history.push("/dashBoardAdmin");
      }
      if (role === ROLES.CCM && !currentPath.startsWith("/dashBoard")) {
        history.push("/dashBoardCCM");
      }
    }
    if (activeItem === "DM" && !currentPath.startsWith(ROUTE_DM)) {
      history.push(ROUTE_DM);
    }
    if (activeItem === "REPORT" && !currentPath.startsWith(ROUTE_REPORT)) {
      history.push(ROUTE_REPORT_BANK);
    }
    if (activeItem === "IB") {
      // Avoid overriding explicit interestBearing sub-routes (e.g., calculationsPage)
      if (location.pathname && location.pathname.startsWith(ROUTE_IB)) {
        return;
      }
      let role = getUserRole(userData);
      role === ROLES.BKP
        ? history.push(ROUTE_IB_BKP)
        : role === ROLES.CCM
          ? history.push(ROUTE_IB_CCM)
          : history.push(ROUTE_IB_BP);
    }
    // Only navigate for Extension if not already on Extension page AND user is not BN role
    if (activeItem === "EXT" && !currentPath.startsWith(ROUTE_EXTENSION)) {
      // Block BN users from accessing extension
      if (
        currentRole === ROLES.BN_ADMIN ||
        currentRole === ROLES.JDU_ADMIN ||
        (currentRole === ROLES.BN_USER && !isAccountingMember)
      ) {
        setActiveItem(currentRole);
        history.push(ROUTE_BN_ERROR);
        return;
      } 
      if(currentRole === ROLES.BN_USER && isAccountingMember) {
        setActiveItem(currentRole);
        history.push(ROUTE_BN_ACCOUNTING);
        return;
      }
      history.push(ROUTE_EXTENSION_BP);
    }
    if (activeItem === ROLES.BN_ADMIN && !currentPath.startsWith(ROUTE_BN)) {
      history.push(ROUTE_BN_ERROR);
    }
    if (activeItem === ROLES.JDU_ADMIN && !currentPath.startsWith(ROUTE_BN)) {
      history.push(ROUTE_BN_ERROR);
    }
    if (activeItem === ROLES.BN_USER && !currentPath.startsWith(ROUTE_BN) && !isAccountingMember) {
      history.push(ROUTE_BN_ERROR);
    }
    if (activeItem === ROLES.BN_USER && !currentPath.startsWith(ROUTE_BN) && isAccountingMember) {
      history.push(ROUTE_BN_ACCOUNTING);
    }   
  }, [activeItem]);

  // Keep activeItem in sync when navigating programmatically
  useEffect(() => {
    const p = location.pathname || "";
    const currentRole = getUserRole(userData);

    // Block BN users from setting activeItem to EXT
    if (p.startsWith("/extension")) {
      if (
        currentRole === ROLES.BN_ADMIN ||
        currentRole === ROLES.JDU_ADMIN ||
        (currentRole === ROLES.BN_USER && !isAccountingMember)
      ) {
        // Redirect BN users away from extension
        setActiveItem(currentRole);
        history.push(ROUTE_BN_ERROR);
        return;
      }if(currentRole === ROLES.BN_USER && isAccountingMember) {
        setActiveItem(currentRole);
        history.push(ROUTE_BN_ACCOUNTING);
        return;
      }
      if (activeItem !== "EXT") setActiveItem("EXT");
    } else if (p.startsWith("/interestBearing")) {
      if (activeItem !== "IB") setActiveItem("IB");
    } else if (p.startsWith("/dataManagement")) {
      if (activeItem !== "DM") setActiveItem("DM");
    } else if (p.startsWith("/report")) {
      if (activeItem !== "REPORT") setActiveItem("REPORT");
    } else if (p.startsWith("/dashBoard") && currentRole === ROLES.BP) {
      if (activeItem !== "DASHBOARD") setActiveItem("DASHBOARD");
    } else if (p.startsWith("/dashBoard") && currentRole === ROLES.ADMIN) {
      if (activeItem !== "DASHBOARD") setActiveItem("DASHBOARD");
    } else if (p.startsWith("/dashBoard") && currentRole === ROLES.CCM) {
      if (activeItem !== "DASHBOARD") setActiveItem("DASHBOARD");
    }
  }, [location.pathname, userData?.userData]);

  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager],
  );

  const LoadTotalErrors = async () => {
    try {
      const response = await getDataWAuth(EXTENSION_URI, {
        view: APP_VIEW.ERRORS,
      });
      if (response.status === 200) {
        const data = await response.data;
        errCxt.setPopUpData(data?.extensionRequestList?.length);
      }
    } catch (error) {
      if (checkErrorMsg(error)) showToast(checkErrorMsg(error), "error");
      else {
        showToast(t("somethingWentWrong"), "error");
      }
    }
  };

  const loadManualNewRequestCount = async () => {
    try {
      const response = await getDataWAuth(MANUAL_REQUEST_LIST);
      const manualRequests = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || response?.data?.extensionRequestList || [];
      setManualNewRequestCount(
        manualRequests.filter((request) => request.requestStatus === "Manual_New").length,
      );
    } catch (error) {
      setManualNewRequestCount(0);
    }
  };

  useEffect(() => {
    const userRole = getUserRole(userData);
    if (
      userRole === ROLES.BKP ||
      userRole === ROLES.BN_ADMIN ||
      userRole === ROLES.JDU_ADMIN ||
      userRole === ROLES.BN_USER
    )
      return;
    if (location.pathname.startsWith("/extension") || activeItem === "EXT") {
      LoadTotalErrors();
      loadManualNewRequestCount();
    }
  }, [activeItem, location.pathname]);

  useEffect(() => {
    const updateManualRequestCount = (event) => {
      setManualNewRequestCount(event.detail);
    };

    window.addEventListener("manualRequestCountUpdated", updateManualRequestCount);
    return () => window.removeEventListener("manualRequestCountUpdated", updateManualRequestCount);
  }, []);

  const extensionAppNav = [
    {
      label: t("businessProcessor"),
      selected:
        ROUTE_EXTENSION_BP === location.pathname ||
        ROUTE_EXTENSION_BP + "/" === location.pathname,
      startIcon: "",
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_EXT, ROUTE_EXTENSION_BP);
        history.push(ROUTE_EXTENSION_BP);
      },
    },
    {
      label: t("jdf"),
      selected:
        ROUTE_EXTENSION_JDF === location.pathname ||
        ROUTE_EXTENSION_JDF + "/" === location.pathname,
      startIcon: "",
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_EXT, ROUTE_EXTENSION_JDF);
        history.push(ROUTE_EXTENSION_JDF);
      },
    },
    {
      label: t("logs"),
      selected:
        ROUTE_EXTENSION_LOGS === location.pathname ||
        ROUTE_EXTENSION_LOGS + "/" === location.pathname,
      startIcon: "",
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_EXT, ROUTE_EXTENSION_LOGS);
        history.push(ROUTE_EXTENSION_LOGS);
      },
    },
    {
      label: <BadgeStat />,
      selected:
        ROUTE_EXTENSION_ERRORS === location.pathname ||
        ROUTE_EXTENSION_ERRORS + "/" === location.pathname,
      startIcon: "",
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_EXT, ROUTE_EXTENSION_ERRORS);
        history.push(ROUTE_EXTENSION_ERRORS);
      },
    },
    {
      label: (
        <div>
          {t("manualRequest")}
          <BadgeStatus
            align="right"
            content={manualNewRequestCount}
            variant="info"
            style={{ marginLeft: "16px", marginRight: "12px" }}
          />
        </div>
      ),
      selected:
        ROUTE_EXTENSION_MANUAL_REQUEST === location.pathname ||
        ROUTE_EXTENSION_MANUAL_REQUEST + "/" === location.pathname,
      startIcon: "",
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_EXT, ROUTE_EXTENSION_MANUAL_REQUEST);
        history.push(ROUTE_EXTENSION_MANUAL_REQUEST);
      },
    },
  ];

  const interestBearingNav = [
    {
      label: t("bankRates"),
      startIcon: "",
      selected:
        ROUTE_IB_BKP === location.pathname ||
        ROUTE_IB_BKP + "/" === location.pathname,
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_IB, ROUTE_IB_BKP);
        history.push(ROUTE_IB_BKP);
      },
    },
    {
      label: t("ratesAllocation"),
      startIcon: "",
      selected: location.pathname.includes(ROUTE_IB_CCM),
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_IB, ROUTE_IB_CCM);
        history.push(ROUTE_IB_CCM);
      },
    },
    {
      label: t("ratesDashboard"),
      startIcon: "",
      selected: location.pathname.includes(ROUTE_IB_BP),
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_IB, ROUTE_IB_BP);
        history.push(ROUTE_IB_BP);
      },
    },
    {
      label: t("calculationPage"),
      startIcon: "",
      selected: location.pathname.includes(ROUTE_IB_CACLC),
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_IB, ROUTE_IB_CACLC);
        history.push(ROUTE_IB_CACLC);
      },
    },
  ];

  const reportNav = [
    {
      label: "Standard Rates",
      startIcon: "",
      selected:
        ROUTE_REPORT_BANK === location.pathname ||
        ROUTE_REPORT_BANK + "/" === location.pathname,
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_REPORT, ROUTE_REPORT_BANK);
        history.push(ROUTE_REPORT_BANK);
      },
    },
    {
      label: "Dealer Rates",
      startIcon: "",
      selected:
        ROUTE_REPORT_DEALER === location.pathname ||
        ROUTE_REPORT_DEALER + "/" === location.pathname,
      onClick: () => {
        sessionStorage.setItem(STORED_ROUTE_REPORT, ROUTE_REPORT_DEALER);
        history.push(ROUTE_REPORT_DEALER);
      },
    },
    {
      label: "Monthly Extensions",
      startIcon: "",
      selected:
        ROUTE_REPORT_MONTHLY_EXTENSION === location.pathname ||
        ROUTE_REPORT_MONTHLY_EXTENSION + "/" === location.pathname,
      onClick: () => {
        sessionStorage.setItem(
          STORED_ROUTE_REPORT,
          ROUTE_REPORT_MONTHLY_EXTENSION,
        );
        history.push(ROUTE_REPORT_MONTHLY_EXTENSION);
      },
    },
    {
      label: "Reason Codes (IE, EF, TI)",
      startIcon: "",
      selected:
        ROUTE_REPORT_MONTHLY_EXTENSION_IEEFTI === location.pathname ||
        ROUTE_REPORT_MONTHLY_EXTENSION_IEEFTI + "/" === location.pathname,
      onClick: () => {
        sessionStorage.setItem(
          STORED_ROUTE_REPORT,
          ROUTE_REPORT_MONTHLY_EXTENSION_IEEFTI,
        );
        history.push(ROUTE_REPORT_MONTHLY_EXTENSION_IEEFTI);
      },
    },
    {
      label: "Monthly Booked Interest",
      startIcon: "",
      selected:
        ROUTE_REPORT_CUSTOMER_INTEREST_DRILLDOWN === location.pathname ||
        ROUTE_REPORT_CUSTOMER_INTEREST_DRILLDOWN + "/" === location.pathname,
      onClick: () => {
        sessionStorage.setItem(
          STORED_ROUTE_REPORT,
          ROUTE_REPORT_CUSTOMER_INTEREST_DRILLDOWN,
        );
        history.push(ROUTE_REPORT_CUSTOMER_INTEREST_DRILLDOWN);
      },
    },
  ];

  const billNowNav = [
    {
      label: "Upload Transactions",
      startIcon: "",
      selected:
        ROUTE_BN_UPLOAD === location.pathname ||
        ROUTE_BN_UPLOAD + "/" === location.pathname,
      onClick: () => {
        history.push(ROUTE_BN_UPLOAD);
      },
    },
    {
      label: "Review and Approve",
      startIcon: "",
      selected:
        ROUTE_BN_ERROR === location.pathname ||
        ROUTE_BN_ERROR + "/" === location.pathname,
      onClick: () => {
        history.push(ROUTE_BN_ERROR);
      },
    },
    {
      label: "Accounting Approval",
      startIcon: "",
      selected:
        ROUTE_BN_ACCOUNTING === location.pathname ||
        ROUTE_BN_ACCOUNTING + "/" === location.pathname,
      onClick: () => {
        history.push(ROUTE_BN_ACCOUNTING);
      },
    },
    {
      label: "Successful Transactions",
      startIcon: "",
      selected:
        ROUTE_BN_SUCESS === location.pathname ||
        ROUTE_BN_SUCESS + "/" === location.pathname,
      onClick: () => {
        history.push(ROUTE_BN_SUCESS);
      },
    },
  ];

  const itemsBillNow =
    getUserRole(userData) === ROLES.BN_ADMIN
      ? [
          {
            label: t("billNow"),
            selected: activeItem === ROLES.BN_ADMIN,
            onClick: () => {
              setActiveItem(ROLES.BN_ADMIN);
              history.push(ROUTE_BN_ERROR);
            },
          },
        ]
      : getUserRole(userData) === ROLES.JDU_ADMIN
        ? [
            {
              label: t("billNow"),
              selected: activeItem === ROLES.JDU_ADMIN,
              onClick: () => {
                setActiveItem(ROLES.JDU_ADMIN);
                history.push(ROUTE_BN_ERROR);
              },
            },
          ]
        : getUserRole(userData) === ROLES.BN_USER && !isAccountingMember
          ? [
              {
                label: t("billNow"),
                selected: activeItem === ROLES.BN_USER,
                onClick: () => {
                  setActiveItem(ROLES.BN_USER);
                  history.push(ROUTE_BN_ERROR);
                },
              },
            ]
        : getUserRole(userData) === ROLES.BN_USER && isAccountingMember
          ? [
              {
                label: t("billNow"),
                selected: activeItem === ROLES.BN_USER,
                onClick: () => {
                  setActiveItem(ROLES.BN_USER);
                  history.push(ROUTE_BN_ACCOUNTING);
                },
              },
            ]
          : [];

  const items =
    itemsBillNow.length > 0
      ? itemsBillNow
      : [
          // Dashboard tab for BP, ADMIN, and CCM
          getUserRole(userData) === ROLES.BP ||
          getUserRole(userData) === ROLES.ADMIN ||
          getUserRole(userData) === ROLES.CCM
            ? {
                label: t("dashboard"),
                selected: activeItem === "DASHBOARD",
                onClick: () => {
                  setActiveItem("DASHBOARD");
                  const role = getUserRole(userData);
                  if (role === ROLES.BP) {
                    history.push("/dashBoardBP");
                  } else if (role === ROLES.ADMIN) {
                    history.push("/dashBoardAdmin");
                  } else if (role === ROLES.CCM) {
                    history.push("/dashBoardCCM");
                  }
                },
              }
            : {},
          getUserRole(userData) !== ROLES.BKP
            ? {
                label: t("extensionApplication"),
                selected: activeItem === "EXT",
                onClick: () => {
                  setActiveItem("EXT");
                  let storedRoute = sessionStorage.getItem(STORED_ROUTE_EXT);
                  if (storedRoute) {
                    history.push(storedRoute);
                    return;
                  }
                  let role = getUserRole(userData);
                  role === ROLES.CCM
                    ? history.push(ROUTE_EXTENSION_JDF)
                    : history.push(ROUTE_EXTENSION_BP);
                },
              }
            : {},
          {
            label: t("interestBearing"),
            selected: activeItem === "IB",
            onClick: () => {
              setActiveItem("IB");
              let storedRoute = sessionStorage.getItem(STORED_ROUTE_IB);
              if (storedRoute) {
                history.push(storedRoute);
                return;
              }
              let role = getUserRole(userData);
              role === ROLES.BKP
                ? history.push(ROUTE_IB_BKP)
                : role === ROLES.CCM
                  ? history.push(ROUTE_IB_CCM)
                  : history.push(ROUTE_IB_CACLC);
            },
          },
          getUserRole(userData) !== ROLES.BKP
            ? {
                label: t("dataManagement"),
                selected:
                  activeItem === "DM" || ROUTE_DM.includes(location.pathname),
                startIcon: "",
                onClick: () => {
                  setActiveItem("DM");
                  history.push(ROUTE_DM);
                },
              }
            : {},
          {
            label: t("report"),
            selected:
              activeItem === "REPORT" ||
              ROUTE_REPORT.includes(location.pathname),
            startIcon: "",
            onClick: () => {
              setActiveItem("REPORT");
              let storedRoute = sessionStorage.getItem(STORED_ROUTE_REPORT);
              if (storedRoute) {
                history.push(storedRoute);
                return;
              }
              history.push(ROUTE_REPORT_BANK);
            },
          },
          getUserRole(userData) === ROLES.ADMIN
            ? {
                label: t("billNow"),
                selected: activeItem === ROLES.BN_ADMIN,
                onClick: () => {
                  setActiveItem(ROLES.BN_ADMIN);
                  history.push(ROUTE_BN_ERROR);
                },
              }
            : {},
        ];

  const data = (
    <div className={styles.navContainer}>
      <div className={styles.nav}>
        <Nav items={items} />
        {activeItem === "EXT" ||
        activeItem === "IB" ||
        activeItem === ROLES.BN_ADMIN ||
        activeItem === ROLES.JDU_ADMIN ||
        activeItem === ROLES.BN_USER ||
        activeItem === "REPORT" ? (
          <div className={styles.nav}>
            <Nav
              items={
                activeItem === "EXT"
                  ? extensionAppNav
                  : activeItem === "IB"
                    ? interestBearingNav
                    : activeItem === "REPORT"
                      ? reportNav
                      : activeItem === ROLES.BN_ADMIN ||
                          activeItem === ROLES.JDU_ADMIN ||
                          activeItem === ROLES.BN_USER
                        ? billNowNav
                        : null
              }
            />
          </div>
        ) : null}
      </div>
      {getUserRole(userData) !== ROLES.BKP &&
        (pathName.includes("extension") ||
          pathName.includes("ratesAllocation")) &&
        showBanner && (
          <div className={styles.nav}>
            <Banner
              bannerMsg={
                pathName.includes("extension")
                  ? EXT_BANNER_MSG
                  : pathName.includes("ratesAllocation")
                    ? IB_CCM_BANNER_MSG
                    : ""
              }
            />
          </div>
        )}
    </div>
  );
  return data;
}
